import { GRAY_1000, MOBILE_VIEW_WIDTH_QUERY, WHITE } from 'constants/styling/theme'
import { Params, SortBy, SortDirection } from 'constants/misc'
import { PropertyListParams, useInfiniteListProperties } from 'dataQueries'
import { CircularProgress, Slide, Stack, useMediaQuery } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDelayedFlag } from 'utils/hooks'
import { useLocation, useNavigate } from 'react-router'

import { Color } from 'constants/assets'
import { GalleryHeader } from 'components/common/Gallery/GalleryHeader'
import { InfiniteList } from 'components/common/InfiniteList/InfiniteList.component'
import { MUIButton } from 'components/common/MUIButton'
import { PageContent } from 'components/common/Page/PageContent'
import { PageHeader } from 'components/common/Page/PageHeader'
import { PageLayout } from 'components/common/Page/PageLayout'
import { PageTransition } from 'utils/animations'
import { Path } from 'constants/router'
import { PropertiesDashboardBulkActions } from '../PropertiesDashboardBulkActions'
import { PropertiesDashboardEmptyContent } from '../PropertiesDashboardEmptyContent'
import { PropertiesDashboardFilter } from '../PropertiesDashboardFilter'
import { PropertiesDashboardPageAction } from '../PropertiesDashboardPageAction'
import { PropertiesDashboardTable } from '../PropertiesDashboardTable'
import { PropertiesDashboardTableRow } from '../PropertiesDashboardTableRow'
import { PropstackPurposeBanner, usePropstackPurposeBannerVisible } from 'components/common/PropstackPurposeBanner'
import { RealEstatePropertyListItemDTO } from 'models/property'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { usePropertiesDashboard } from '../contexts/PropertiesDashboard.context'
import { useDebouncedMemo } from 'utils/helpers'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Page listing properties of logged in client with search and filter options.
 * @example <PropertiesDashboardController />
 */
export const PropertiesDashboardController: React.FC = () => {
  const { t } = useTranslation(['properties_dashboard', 'workspace_settings'])
  const navigate = useNavigate()
  const location = useLocation()
  const params = useBkbnSearchParams()
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const { spawnWarningToast } = useSnackbar()

  const { userPropertyWorkspaceId, isWorkspaceResolved } = useUserData()
  const { clearSelection, selectedIds } = usePropertiesDashboard()

  const [isPropertyDashboardEmpty, setIsPropertyDashboardEmpty] = useState(false)

  // Show a loading wheel on the properties list if workspace resolution takes longer than 1s
  const showWorkspaceLoader = useDelayedFlag(!isWorkspaceResolved, 1000)

  useEffect(() => {
    if (location.state?.workspaceAccessDenied) {
      spawnWarningToast(t('workspace_settings:access_denied'))
      window.history.replaceState({}, '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  // SEARCH DEBOUNCE HANDLING
  const search = useMemo(() => params.get(Params.SEARCH), [params])
  const brokerId = useMemo(() => params.get(Params.BROKER_ID), [params])
  const creationDateStart = useMemo(() => params.get(Params.PROPERTY_CREATION_DATE_START), [params])
  const creationDateEnd = useMemo(() => params.get(Params.PROPERTY_CREATION_DATE_END), [params])
  const orderStatus = useMemo(() => params.get(Params.PROPERTY_ORDER_STATUS), [params])
  const debouncedSearch = useDebouncedMemo(() => search, [search], 300)

  // SORTING PARAMS
  const sortBy = useMemo(() => {
    const sortByParam = params.get(Params.SORT_BY)
    if (sortByParam === SortBy.PROPERTY_TITLE || sortByParam === SortBy.PROPERTY_CREATION_DATE || sortByParam === SortBy.LAST_UPDATED_DATE) {
      return sortByParam
    }
    return undefined
  }, [params])

  const sortDirection = useMemo(() => {
    const sortDirectionParam = params.get(Params.SORT_DIRECTION)
    if (sortDirectionParam === SortDirection.ASCENDING || sortDirectionParam === SortDirection.DESCENDING) {
      return sortDirectionParam
    }
    return undefined
  }, [params])

  // PARAMS
  const propertiesParams = useMemo<PropertyListParams>(() => ({
    searchText: debouncedSearch ?? '',
    brokerId: brokerId ?? undefined,
    creationDateStart: creationDateStart ?? undefined,
    creationDateEnd: creationDateEnd ?? undefined,
    orderStatus: orderStatus ?? undefined,
    size: 25,
    sortBy,
    sortDirection,
  }), [brokerId, debouncedSearch, creationDateStart, creationDateEnd, sortBy, sortDirection, orderStatus])

  // PROPERTY LIST
  const propertyList = useInfiniteListProperties(userPropertyWorkspaceId, propertiesParams)

  // IDs of properties currently rendered — used by the header "select all" checkbox.
  const visibleIds = useMemo(
    () => propertyList.data?.pages.flatMap(page => page.data.content.map(item => item.id)) ?? [],
    [propertyList.data]
  )

  // Broker email shared by every selected property (undefined when none selected or the selection spans multiple brokers).
  // Lets the reassign popup flag the already-assigned broker as non-selectable.
  const selectedBrokerEmail = useMemo(() => {
    const loaded = propertyList.data?.pages.flatMap(page => page.data.content) ?? []
    const emails = new Set(
      loaded
        .filter(item => selectedIds.has(item.id))
        .map(item => item.broker?.email)
        .filter(Boolean)
    )
    return emails.size === 1 ? [...emails][0] : undefined
  }, [propertyList.data, selectedIds])

  // Refetch property list and clear selection when workspace id changes
  useEffect(() => {
    if (!userPropertyWorkspaceId) return
    clearSelection()
    propertyList.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPropertyWorkspaceId])

  // Scroll to top with smooth animation when sorting changes
  useEffect(() => {
    if (sortBy) {
      const scrollToTop = () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }

      // Small delay to ensure the DOM has updated
      const timeoutId = setTimeout(scrollToTop, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [sortBy, sortDirection])

  useEffect(() => {
    if (propertyList.isSuccess) {
      const hasActiveFilters = search || brokerId || creationDateStart || creationDateEnd || orderStatus

      if (propertyList.data?.pages[0].data.empty && !hasActiveFilters) {
        setIsPropertyDashboardEmpty(true)
      } else {
        setIsPropertyDashboardEmpty(false)
      }
    }

    // Check if the property dashboard is empty based on data and active filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyList.data?.pages, search, brokerId, creationDateStart, creationDateEnd, orderStatus])

  const showPropstackBanner = usePropstackPurposeBannerVisible()

  const handleSortChange = (newSortBy: SortBy) => {
    // If clicking the same column, toggle direction
    if (sortBy && newSortBy === sortBy) {
      const newDirection = sortDirection === SortDirection.ASCENDING
        ? SortDirection.DESCENDING
        : SortDirection.ASCENDING
      params.set([Params.SORT_DIRECTION, newDirection])
    } else {
      // If clicking a different column or no sorting is active, set it as the new sort by and default to ascending
      params.set([Params.SORT_BY, newSortBy], [Params.SORT_DIRECTION, SortDirection.ASCENDING])
    }
  }

  return (
    <PageTransition>
      <PageLayout
        header={
          <PageHeader
            actions={!isMobileView && <PropertiesDashboardPageAction />}
          >
            <GalleryHeader title={t('properties')} />
          </PageHeader>
        }
        layoutErrorContent={null}
      >
        <Stack width="100%" alignItems="center" justifyContent="center">

          {!isWorkspaceResolved && showWorkspaceLoader &&
            <Stack direction="row" justifyContent="center" width="100%" sx={{ height: isMobileView ? 'calc(100vh - 2.4rem)' : 'calc(100vh - 4rem - 15rem)' }}>
              <CircularProgress sx={{ color: Color.GRAY_DISABLED, marginY: 'auto' }} />
            </Stack>
          }

          {isWorkspaceResolved && isPropertyDashboardEmpty &&
            <>
              {showPropstackBanner &&
                <PageContent>
                  <PropstackPurposeBanner sx={{ marginTop: '4rem' }} />
                </PageContent>
              }

              <PropertiesDashboardEmptyContent />
            </>
          }

          {isWorkspaceResolved && !isPropertyDashboardEmpty &&
            <PageContent>
              <Stack
                sx={{
                  height: isMobileView ? 'calc(100vh - 2.4rem)' : 'calc(100vh - 4rem - 15rem)',
                  padding: isMobileView ? '2.4rem 0' : '4rem 0', overflowY: 'auto'
                }}
              >

                <PropstackPurposeBanner sx={{ marginBottom: '2.4rem' }} />

                <PropertiesDashboardFilter />

                <PropertiesDashboardBulkActions currentBrokerEmail={selectedBrokerEmail} />

                <InfiniteList<RealEstatePropertyListItemDTO>
                  query={propertyList}
                  customRenderItem={(data) => (
                    <PropertiesDashboardTable
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      onSortChange={handleSortChange}
                      visibleIds={visibleIds}
                    >
                      {data.map(item => <PropertiesDashboardTableRow key={item.id} property={item} />)}
                    </PropertiesDashboardTable>
                  )}
                  sx={{
                    boxSizing: 'border-box',
                    width: '100%',
                    height: '100%',
                    borderRadius: '.8rem',
                    overflowY: 'auto',
                    flex: 1,
                  }}
                />

              </Stack>
            </PageContent>
          }
        </Stack>

        {/* Add Property floating bar */}
        <Slide direction="up" in={isMobileView} mountOnEnter unmountOnExit>
          <Stack
            width="100vw"
            position="fixed"
            boxSizing="border-box"
            bottom="1.6rem"
            left={0}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Stack
              width={'auto'}
              padding=".4rem .4rem"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              borderRadius="4.8rem"
              bgcolor={GRAY_1000}
              fontSize="1.4rem"
              fontWeight={600}
              color={WHITE}
              boxSizing="border-box"
            >

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                height="100%"
                fontWeight={600}
              >
                <MUIButton
                  type="defaultPrimary"
                  onClick={() => navigate(Path.NEW_PROPERTIES)}
                >
                  {t('add_property')}
                </MUIButton>
              </Stack>

            </Stack>
          </Stack>
        </Slide>

      </PageLayout>
    </PageTransition>
  )
}
